/**
 * Session Manager for Monday.com iframe authentication persistence
 * Handles session tokens to maintain authentication across iframe reloads
 */

class SessionManager {
    constructor() {
        this.tokenKey = 'monday_session_token';
        this.storageKey = 'monday_session_data';
        this.refreshInterval = null;
        this.refreshTimeout = 30 * 60 * 1000; // 30 minutes - refresh session before timeout
    }

    /**
     * Store session token in localStorage and set cookie
     */
    setSessionToken(token) {
        try {
            // Store in localStorage (iframe-friendly)
            localStorage.setItem(this.tokenKey, token);
            
            // Also set as cookie for server-side access
            this.setCookie(this.tokenKey, token, 30);
            
            console.log('🔑 Session token stored successfully');
            return true;
        } catch (error) {
            console.error('❌ Failed to store session token:', error);
            return false;
        }
    }

    /**
     * Get session token from localStorage
     */
    getSessionToken() {
        try {
            return localStorage.getItem(this.tokenKey);
        } catch (error) {
            console.error('❌ Failed to get session token:', error);
            return null;
        }
    }

    /**
     * Clear session token from storage
     */
    clearSessionToken() {
        try {
            localStorage.removeItem(this.tokenKey);
            this.deleteCookie(this.tokenKey);
            this.stopSessionRefresh();
            console.log('🔑 Session token cleared');
            return true;
        } catch (error) {
            console.error('❌ Failed to clear session token:', error);
            return false;
        }
    }

    /**
     * Complete logout process - clears all session data
     */
    logout() {
        console.log('🚪 Starting logout process...');
        
        // Clear all session tokens
        this.clearSessionToken();
        
        // Clear any additional session data
        localStorage.removeItem(this.storageKey);
        
        // Clear all Monday.com related data
        const mondayKeys = Object.keys(localStorage).filter(key =>
            key.includes('monday') || key.includes('session')
        );
        mondayKeys.forEach(key => localStorage.removeItem(key));
        
        console.log('✅ Logout completed - all session data cleared');
    }

    /**
     * Check if session token exists
     */
    hasSessionToken() {
        return !!this.getSessionToken();
    }

    /**
     * Add session token to URL parameters
     */
    addTokenToUrl(url) {
        const token = this.getSessionToken();
        if (!token) return url;

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}session_token=${encodeURIComponent(token)}`;
    }

    /**
     * Handle session token from URL parameters (after OAuth redirect)
     */
    handleUrlToken() {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('session_token');
        
        if (token) {
            console.log('🔑 Session token found in URL, storing...');
            this.setSessionToken(token);
            
            // Clean URL by removing the token parameter
            this.cleanUrl();
            
            return true;
        }
        return false;
    }

    /**
     * Clean URL by removing session token parameter
     */
    cleanUrl() {
        const url = new URL(window.location);
        url.searchParams.delete('session_token');
        window.history.replaceState({}, '', url.toString());
    }

    /**
     * Set cookie with expiration
     */
    setCookie(name, value, days) {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        
        // Enhanced cookie settings for iframe compatibility
        let cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/`;
        
        // Add SameSite and Secure attributes for cross-origin iframe support
        cookieString += ';SameSite=None;Secure';
        
        // For maximum compatibility, also set domain if available
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
            cookieString += `;domain=.${window.location.hostname}`;
        }
        
        document.cookie = cookieString;
        console.log('🍪 Cookie set:', name, 'for', days, 'days');
    }

    /**
     * Delete cookie
     */
    deleteCookie(name) {
        let cookieString = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=None;Secure`;
        
        // For maximum compatibility, also set domain if available
        if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
            cookieString += `;domain=.${window.location.hostname}`;
        }
        
        document.cookie = cookieString;
        console.log('🍪 Cookie deleted:', name);
    }

    /**
     * Listen for session token messages from popup windows
     */
    setupMessageListener() {
        window.addEventListener('message', (event) => {
            if (event.data.type === 'session_token') {
                console.log('🔑 Received session token from popup');
                this.setSessionToken(event.data.token);
                
                // Check if we're in an iframe and need to update the parent's URL with the token
                if (window.parent !== window) {
                    console.log('🔗 In iframe, enhancing parent URL with session token...');
                    
                    // Get the current URL and add the session token
                    const currentUrl = new URL(window.location);
                    currentUrl.searchParams.set('session_token', event.data.token);
                    
                    // Try to update the parent window's URL without reloading
                    try {
                        window.parent.history.replaceState({}, '', currentUrl.toString());
                        console.log('✅ Parent URL enhanced with session token');
                    } catch (e) {
                        console.warn('⚠️ Could not update parent URL (cross-origin restriction):', e.message);
                    }
                }
                
                // Reload the page to apply the new session
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            }
        });
    }

    /**
     * Initialize session manager
     */
    init() {
        this.setupMessageListener();
        
        // First, try to handle Monday.com authentication
        if (this.handleMondayAuth()) {
            console.log('🔑 Monday.com authentication detected');
        } else {
            // Fall back to regular session token handling
            this.handleUrlToken();
        }
        
        // Add session token to all internal links for iframe persistence
        this.enhanceLinks();
        
        // Start session refresh interval if user is authenticated
        this.startSessionRefresh();
        
        console.log('🔑 Session Manager initialized');
    }

    /**
     * Enhance all internal links to include session token
     */
    enhanceLinks() {
        // This will be called after page load to enhance navigation
        setTimeout(() => {
            const links = document.querySelectorAll('a[href^="/"]');
            links.forEach(link => {
                const originalHref = link.getAttribute('href');
                if (originalHref && !originalHref.includes('session_token')) {
                    const enhancedHref = this.addTokenToUrl(originalHref);
                    link.setAttribute('href', enhancedHref);
                    
                    // Also add click handler for links that might bypass href changes
                    link.addEventListener('click', (e) => {
                        // Prevent default if we need to handle navigation manually
                        if (window.parent !== window) {
                            e.preventDefault();
                            const targetUrl = this.addTokenToUrl(originalHref);
                            console.log('🔗 Navigating to:', targetUrl);
                            window.location.href = targetUrl;
                        }
                    });
                }
            });
            
            // Also enhance form actions
            const forms = document.querySelectorAll('form[action^="/"]');
            forms.forEach(form => {
                const originalAction = form.getAttribute('action');
                if (originalAction && !originalAction.includes('session_token')) {
                    const enhancedAction = this.addTokenToUrl(originalAction);
                    form.setAttribute('action', enhancedAction);
                }
            });
        }, 100);
    }

    /**
     * Start session refresh interval to keep session alive
     */
    startSessionRefresh() {
        if (this.hasSessionToken()) {
            console.log('🔄 Starting session refresh interval (every 30 minutes)');
            
            // Refresh immediately
            this.refreshSession();
            
            // Set up periodic refresh
            this.refreshInterval = setInterval(() => {
                this.refreshSession();
            }, this.refreshTimeout);
        }
    }

    /**
     * Refresh session by making a lightweight API call
     */
    refreshSession() {
        if (!this.hasSessionToken()) {
            this.stopSessionRefresh();
            return;
        }

        console.log('🔄 Refreshing session...');
        
        // Make a lightweight request to keep session alive
        fetch('/api/session/keepalive', {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'include' // Include cookies for session
        })
        .then(response => {
            if (response.ok) {
                console.log('✅ Session refreshed successfully');
            } else {
                console.warn('⚠️ Session refresh failed:', response.status);
            }
        })
        .catch(error => {
            console.error('❌ Session refresh error:', error);
        });
    }

    /**
     * Stop session refresh interval
     */
    stopSessionRefresh() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
            console.log('🛑 Stopped session refresh interval');
        }
    }

    /**
     * Handle Monday.com authentication token from URL parameters
     * This allows automatic reauthentication after browser restart
     */
    handleMondayAuth() {
        const urlParams = new URLSearchParams(window.location.search);
        const mondayToken = urlParams.get('sessionToken');
        
        if (mondayToken) {
            console.log('🔑 Monday.com session token found, attempting automatic authentication...');
            
            try {
                // The Monday.com token is a JWT, we need to decode it to get user info
                const payload = this.decodeJwt(mondayToken);
                if (payload && payload.dat) {
                    const userData = payload.dat;
                    console.log('👤 Monday.com user data:', userData);
                    
                    // Send the Monday.com token to the server for authentication
                    this.authenticateWithMondayToken(mondayToken, userData);
                    return true;
                }
            } catch (error) {
                console.error('❌ Failed to decode Monday.com token:', error);
            }
        }
        return false;
    }

    /**
     * Decode JWT token without verification (for Monday.com tokens)
     */
    decodeJwt(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('❌ JWT decoding failed:', error);
            return null;
        }
    }

    /**
     * Authenticate using Monday.com token by sending it to the server
     */
    authenticateWithMondayToken(mondayToken, userData) {
        fetch('/api/monday/authenticate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({
                token: mondayToken,
                user_data: userData
            })
        })
        .then(response => response.json())
        .then(data => {
            if (data.success && data.session_token) {
                console.log('✅ Monday.com authentication successful');
                this.setSessionToken(data.session_token);
                
                // Clean monday token params from URL before reload
                const url = new URL(window.location.href);
                url.searchParams.delete('sessionToken');
                url.searchParams.delete('session_token');
                window.history.replaceState({}, '', url.toString());
                console.log('🧹 Token params cleaned from URL');
                
                // Reload to apply the new session
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } else {
                console.warn('⚠️ Monday.com authentication failed:', data.message);
            }
        })
        .catch(error => {
            console.error('❌ Monday.com authentication error:', error);
        });
    }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager;